/** A small picture of how a layer is drawn, on the map's own background, from its style definition. */
import { useId } from 'react';
import { MAP, speciesColor } from '@/theme/colors';
import { brand } from '@/theme/theme';
import type { Sample } from './layerCatalog';
import { GENUS_FILL } from './layers';

const W = 26;
const H = 16;

export function LayerSample({ sample, testId }: { sample: Sample; testId?: string }) {
  const gradient = useId();
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true" data-testid={testId} data-sample={sample.kind} style={{ flex: 'none' }}>
      <rect x={0.5} y={0.5} width={W - 1} height={H - 1} rx={3} fill={sample.kind === 'swatch' ? sample.color : MAP.background} stroke={brand.border} data-color={sample.kind === 'swatch' ? sample.color : undefined} />
      {sample.kind === 'line' && (
        <line
          x1={4}
          y1={H / 2}
          x2={W - 4}
          y2={H / 2}
          stroke={sample.look.color}
          strokeWidth={Math.min(sample.look.width, 4)}
          strokeDasharray={sample.look.dash?.map((d) => d * Math.min(sample.look.width, 4)).join(' ')}
          data-color={sample.look.color}
        />
      )}
      {sample.kind === 'point' && (
        <circle
          cx={W / 2}
          cy={H / 2}
          r={4.5}
          fill={sample.look.fill === null ? 'none' : sample.look.fill === GENUS_FILL ? (sample.color ?? speciesColor('Unknown')) : sample.look.fill}
          stroke={sample.look.stroke}
          strokeWidth={Math.max(sample.look.strokeWidth, 0.75)}
          data-color={sample.look.fill === null ? '' : sample.color}
        />
      )}
      {sample.kind === 'crown' &&
        sample.colors.map((color, i) => <circle key={color + i} cx={7 + i * 6} cy={i % 2 ? 6 : 10} r={4.5} fill={color} fillOpacity={0.75} stroke={sample.outline} strokeWidth={0.75} />)}
      {sample.kind === 'fill' && <rect x={5} y={4} width={W - 10} height={H - 8} rx={2} fill={sample.color} fillOpacity={0.6} stroke={sample.color} data-color={sample.color} />}
      {sample.kind === 'ramp' && (
        <>
          <defs>
            <linearGradient id={gradient}>
              {sample.colors.map((c, i) => (
                <stop key={i} offset={`${(100 * i) / Math.max(1, sample.colors.length - 1)}%`} stopColor={c} />
              ))}
            </linearGradient>
          </defs>
          <rect x={4} y={5} width={W - 8} height={H - 10} rx={2} fill={`url(#${gradient})`} />
        </>
      )}
    </svg>
  );
}
